import { CnpjDetail } from "@/components/CnpjDetail";
import { SocioEmpresas, socioSearchParams } from "@/components/SocioEmpresas";
import { Button } from "@/components/ui/button";
import { DEFAULT_PAGE_SIZE } from "@/lib/filters";
import { formatCnpj } from "@/lib/format";
import { UI } from "@/lib/labels";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowUpRight, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

interface CnpjDrawerProps {
  cnpj: string | null;
  open: boolean;
  onClose: () => void;
  /** URL da lista — origem dos links internos e do "Abrir página completa". */
  listUrl: string;
}

/**
 * Visões navegáveis dentro do drawer. A navegação interna (clicar num sócio,
 * num outro estabelecimento ou numa empresa do sócio) empilha visões aqui,
 * sem alterar a rota da página — a lista atrás permanece intacta.
 */
type DrawerView =
  | { type: "cnpj"; cnpj: string }
  | { type: "socio"; nome: string; documento: string | null; page: number; pageSize: number };

/** URL da página completa equivalente à visão (para abrir fora do drawer). */
function viewUrl(view: DrawerView): string {
  if (view.type === "cnpj") {
    return `/cnpj/${view.cnpj}`;
  }
  return `/socio?${socioSearchParams(view.nome, view.documento)}`;
}

/** Rótulo acessível do dialog conforme a visão exibida. */
function viewLabel(view: DrawerView): string {
  if (view.type === "cnpj") {
    return `Detalhe do CNPJ ${formatCnpj(view.cnpj)}`;
  }
  return `Empresas do sócio ${view.nome || view.documento || ""}`.trim();
}

/**
 * Drawer lateral direito com a ficha completa do CNPJ. Abre sem navegar (a
 * lista atrás fica intacta) e navega internamente: sócios e outros
 * estabelecimentos abrem dentro do próprio painel, com botão voltar
 * (Cmd/Ctrl+clique segue abrindo a página completa). Fecha por ✕, ESC ou
 * clique no overlay. Acessível (role="dialog", foco preso, restaura o foco
 * ao fechar).
 */
export function CnpjDrawer({ cnpj, open, onClose, listUrl }: CnpjDrawerProps) {
  const [rendered, setRendered] = useState(false);
  const [visible, setVisible] = useState(false);
  const [stack, setStack] = useState<DrawerView[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // (Re)inicia a pilha na raiz ao abrir ou trocar de registro com o drawer
  // aberto. Ao fechar (open=false) a pilha é preservada para a animação de
  // saída não esvaziar o conteúdo.
  useEffect(() => {
    if (open && cnpj) {
      setStack([{ type: "cnpj", cnpj }]);
    }
  }, [open, cnpj]);

  const currentView = stack.length > 0 ? stack[stack.length - 1] : null;

  const pushView = useCallback((view: DrawerView) => {
    setStack((previous) => [...previous, view]);
  }, []);

  const popView = useCallback(() => {
    setStack((previous) => (previous.length > 1 ? previous.slice(0, -1) : previous));
  }, []);

  /** Atualiza a paginação quando a visão do topo é a de sócio. */
  const patchSocioView = useCallback((patch: { page?: number; pageSize?: number }) => {
    setStack((previous) => {
      const top = previous[previous.length - 1];
      if (!top || top.type !== "socio") {
        return previous;
      }
      return [...previous.slice(0, -1), { ...top, ...patch }];
    });
  }, []);

  const openCnpjView = useCallback(
    (value: string) => pushView({ type: "cnpj", cnpj: value }),
    [pushView],
  );

  const openSocioView = useCallback(
    (nome: string, documento: string | null) =>
      pushView({ type: "socio", nome, documento, page: 1, pageSize: DEFAULT_PAGE_SIZE }),
    [pushView],
  );

  // Navegação interna rola o conteúdo para o topo (inclusive paginação).
  // biome-ignore lint/correctness/useExhaustiveDependencies: stack é o gatilho real do scroll (push/pop/paginação).
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [stack]);

  // Animação de entrada/saída (monta, desliza; ao fechar, desliza e desmonta).
  useEffect(() => {
    if (open) {
      setRendered(true);
      // setTimeout (e não requestAnimationFrame) para o slide-in disparar de
      // forma confiável mesmo com a aba em segundo plano.
      const timer = window.setTimeout(() => setVisible(true), 20);
      return () => window.clearTimeout(timer);
    }
    setVisible(false);
    const timer = window.setTimeout(() => setRendered(false), 300);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Foco, ESC, trava de scroll e restauração de foco enquanto renderizado.
  useEffect(() => {
    if (!rendered) {
      return;
    }
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) {
          return;
        }
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    const focusTimer = window.setTimeout(() => panelRef.current?.focus(), 50);

    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(focusTimer);
      restoreFocusRef.current?.focus();
    };
  }, [rendered, onClose]);

  if (!rendered || !currentView) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Fechar"
        tabIndex={-1}
        onClick={onClose}
        className={cn(
          "absolute inset-0 bg-slate-900/50 transition-opacity duration-300",
          visible ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={viewLabel(currentView)}
        tabIndex={-1}
        className={cn(
          // Largura: 100% da viewport em telas pequenas (< md); em desktop ~70vw
          // com piso de 640px e teto de 1100px, dando espaço para as seções da
          // ficha renderizarem em 2 colunas (lg:grid-cols-2 no CnpjDetail).
          "absolute right-0 top-0 flex h-full w-full flex-col bg-background shadow-2xl transition-transform duration-300 ease-out focus:outline-none md:w-[70vw] md:min-w-[640px] md:max-w-[1100px]",
          visible ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-card px-5 py-3">
          <div className="flex min-w-0 items-center gap-3">
            {stack.length > 1 ? (
              <Button variant="ghost" size="sm" onClick={popView}>
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {UI.actions.back}
              </Button>
            ) : null}
            <Link
              to={viewUrl(currentView)}
              state={{ from: listUrl }}
              onClick={onClose}
              className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
            >
              Abrir página completa
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fechar">
            <X className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
        <div ref={contentRef} className="flex-1 overflow-y-auto p-5">
          {currentView.type === "cnpj" ? (
            <CnpjDetail
              cnpj={currentView.cnpj}
              from={listUrl}
              onOpenSocio={openSocioView}
              onOpenCnpj={openCnpjView}
            />
          ) : (
            <SocioEmpresas
              nome={currentView.nome}
              documento={currentView.documento}
              page={currentView.page}
              pageSize={currentView.pageSize}
              onPageChange={(page) => patchSocioView({ page })}
              onPageSizeChange={(pageSize) => patchSocioView({ page: 1, pageSize })}
              from={listUrl}
              onOpenCnpj={openCnpjView}
            />
          )}
        </div>
      </div>
    </div>,
    // Portal para dentro do container do React (#root): o React 18 delega os
    // eventos no container raiz, então cliques no overlay/✕ precisam borbulhar
    // até ele. Portal em document.body (fora do #root) não recebe o onClick.
    document.getElementById("root") ?? document.body,
  );
}
